import { msalInstance, apiRequest } from '../config/authConfig'
import { apiConfig } from '../config/apiConfig'
import type {
  Application,
  AppOwner,
  AppPermissionsData,
  AppHistoryData,
  Credential,
  Owner,
  BusinessUnit,
  RiskLevel,
  SsoMode,
  AuditLogEntry,
  CredentialSummary,
  ApplicationFormData,
  OwnerFormData,
  BusinessUnitFormData,
  CredentialRenewalFormData,
  PagedResponse,
  TrendData,
  ApplicationChangesData,
  ChangeDetail,
  PostureData,
  SyncHealth,
  ChangeAcknowledgment,
  CACoverageData,
  RecentChangeFeedData,
} from '../types'

class ApiService {
  private async getAccessToken(): Promise<string> {
    // Always acquire tokens for the *active* account — never accounts[0],
    // which is just whichever account MSAL cached first. After "Switch
    // account" both old and new identities live in the cache, and using
    // accounts[0] would acquire a token for the wrong user (and fire an
    // ACQUIRE_TOKEN_SUCCESS event that races with the active-account
    // pointer).
    const account = msalInstance.getActiveAccount()
    if (!account) {
      throw new Error('No active account')
    }

    try {
      const response = await msalInstance.acquireTokenSilent({
        ...apiRequest,
        account,
      })
      return response.accessToken
    } catch (error) {
      const response = await msalInstance.acquireTokenPopup(apiRequest)
      return response.accessToken
    }
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const token = await this.getAccessToken()
    const url = `${apiConfig.baseUrl}${endpoint}`

    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...options.headers,
      },
    })

    if (!response.ok) {
      const errorBody = await response.text()
      throw new Error(`API Error: ${response.status} - ${errorBody}`)
    }

    // Handle empty responses
    const text = await response.text()
    if (!text) return {} as T

    return JSON.parse(text)
  }

  // Applications
  async getApplications(params?: {
    page?: number
    pageSize?: number
    search?: string
    businessUnitId?: number
    riskLevelId?: number
    hasOwner?: boolean
    ssoMode?: string
    includeDeleted?: boolean
  }): Promise<PagedResponse<Application>> {
    const searchParams = new URLSearchParams()
    if (params?.page) searchParams.set('page', params.page.toString())
    if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString())
    if (params?.search) searchParams.set('search', params.search)
    if (params?.businessUnitId) searchParams.set('businessUnitId', params.businessUnitId.toString())
    if (params?.riskLevelId) searchParams.set('riskLevelId', params.riskLevelId.toString())
    if (params?.hasOwner !== undefined) searchParams.set('hasOwner', params.hasOwner.toString())
    if (params?.ssoMode) searchParams.set('ssoMode', params.ssoMode)
    if (params?.includeDeleted) searchParams.set('includeDeleted', 'true')

    const query = searchParams.toString()
    return this.request<PagedResponse<Application>>(
      `${apiConfig.endpoints.applications}${query ? `?${query}` : ''}`
    )
  }

  async getSsoModes(): Promise<SsoMode[]> {
    return this.request<SsoMode[]>(apiConfig.endpoints.ssoModes)
  }

  async getApplication(id: number): Promise<Application> {
    return this.request<Application>(apiConfig.endpoints.applicationById(id))
  }

  async updateApplication(id: number, data: ApplicationFormData): Promise<Application> {
    return this.request<Application>(apiConfig.endpoints.applicationById(id), {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async getApplicationCredentials(id: number): Promise<Credential[]> {
    return this.request<Credential[]>(apiConfig.endpoints.applicationCredentials(id))
  }

  async getApplicationOwners(appId: number): Promise<AppOwner[]> {
    return this.request<AppOwner[]>(apiConfig.endpoints.applicationOwners(appId))
  }

  async addApplicationOwner(appId: number, ownerId: number, isPrimary: boolean): Promise<AppOwner[]> {
    return this.request<AppOwner[]>(apiConfig.endpoints.applicationOwners(appId), {
      method: 'POST',
      body: JSON.stringify({ ownerId, isPrimary }),
    })
  }

  async removeApplicationOwner(appId: number, ownerId: number): Promise<AppOwner[]> {
    return this.request<AppOwner[]>(apiConfig.endpoints.applicationOwnerById(appId, ownerId), {
      method: 'DELETE',
    })
  }

  async setPrimaryOwner(appId: number, ownerId: number): Promise<AppOwner[]> {
    return this.request<AppOwner[]>(apiConfig.endpoints.applicationOwnerById(appId, ownerId), {
      method: 'PUT',
    })
  }

  async getApplicationPermissions(appId: number): Promise<AppPermissionsData> {
    return this.request<AppPermissionsData>(apiConfig.endpoints.applicationPermissions(appId))
  }

  async getApplicationHistory(appId: number): Promise<AppHistoryData> {
    return this.request<AppHistoryData>(apiConfig.endpoints.applicationHistory(appId))
  }

  // Credentials
  async getCredentials(params?: {
    page?: number
    pageSize?: number
    applicationId?: number
    credentialType?: string
    expiringWithinDays?: number
    includeExpired?: boolean
  }): Promise<PagedResponse<Credential>> {
    const searchParams = new URLSearchParams()
    if (params?.page) searchParams.set('page', params.page.toString())
    if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString())
    if (params?.applicationId) searchParams.set('applicationId', params.applicationId.toString())
    if (params?.credentialType) searchParams.set('credentialType', params.credentialType)
    if (params?.expiringWithinDays) searchParams.set('expiringWithinDays', params.expiringWithinDays.toString())
    if (params?.includeExpired) searchParams.set('includeExpired', 'true')

    const query = searchParams.toString()
    return this.request<PagedResponse<Credential>>(
      `${apiConfig.endpoints.credentials}${query ? `?${query}` : ''}`
    )
  }

  async getCredential(id: number): Promise<Credential> {
    return this.request<Credential>(apiConfig.endpoints.credentialById(id))
  }

  async getExpiringCredentials(daysAhead: number = 30): Promise<Credential[]> {
    return this.request<Credential[]>(
      `${apiConfig.endpoints.expiringCredentials}?daysAhead=${daysAhead}`
    )
  }

  async getCredentialSummary(): Promise<CredentialSummary[]> {
    return this.request<CredentialSummary[]>(apiConfig.endpoints.credentialSummary)
  }

  async updateCredentialRenewal(id: number, data: CredentialRenewalFormData): Promise<void> {
    await this.request<void>(apiConfig.endpoints.credentialById(id), {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  async markCredentialRenewed(id: number): Promise<void> {
    await this.request<void>(`${apiConfig.endpoints.credentialById(id)}/renewed`, {
      method: 'POST',
    })
  }

  // Owners
  async getOwners(params?: {
    page?: number
    pageSize?: number
    search?: string
    isActive?: boolean
    accountStatus?: string
    includeDeparted?: boolean
  }): Promise<PagedResponse<Owner>> {
    const searchParams = new URLSearchParams()
    if (params?.page) searchParams.set('page', params.page.toString())
    if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString())
    if (params?.search) searchParams.set('search', params.search)
    if (params?.isActive !== undefined) searchParams.set('isActive', params.isActive.toString())
    if (params?.accountStatus) searchParams.set('accountStatus', params.accountStatus)
    if (params?.includeDeparted) searchParams.set('includeDeparted', 'true')

    const query = searchParams.toString()
    return this.request<PagedResponse<Owner>>(
      `${apiConfig.endpoints.owners}${query ? `?${query}` : ''}`
    )
  }

  async getOwnerLifecycleSummary(): Promise<{
    statusCounts: Array<{ accountStatus: string; ownerCount: number }>
    applicationsWithoutOwners: Array<{
      applicationId: number
      displayName: string
      businessUnitName?: string
      riskLevelName?: string
      riskLevelColor?: string
    }>
    applicationsWithoutOwnersCount: number
    recentlyDepartedOwners: Array<{
      ownerId: number
      displayName: string
      email: string
      accountStatus: string
      departedAt: string
    }>
    lastSync?: {
      lastSyncAt: string
      details?: {
        totalOwners: number
        verifiedOwners: number
        departedOwners: number
        promotedOwners: number
        applicationsAffected: number
        applicationsWithoutOwners: number
        durationSeconds: number
      }
    }
  }> {
    return this.request('/owners/lifecycle-summary')
  }

  async syncOwnersFromEntraID(): Promise<{
    success: boolean
    totalOwners: number
    verifiedOwners: number
    departedOwners: number
    promotedOwners: number
    applicationsAffected: number
    applicationsWithoutOwners: number
    durationSeconds: number
    errors?: string[]
    errorMessage?: string
  }> {
    return this.request('/sync/owners', {
      method: 'POST',
    })
  }

  async getOwner(id: number): Promise<Owner> {
    return this.request<Owner>(apiConfig.endpoints.ownerById(id))
  }

  async createOwner(data: OwnerFormData): Promise<Owner> {
    return this.request<Owner>(apiConfig.endpoints.owners, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async updateOwner(id: number, data: OwnerFormData): Promise<Owner> {
    return this.request<Owner>(apiConfig.endpoints.ownerById(id), {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async deleteOwner(id: number): Promise<void> {
    await this.request<void>(apiConfig.endpoints.ownerById(id), {
      method: 'DELETE',
    })
  }

  async syncUsersFromAzureAD(): Promise<{ message: string; stats: { total_fetched: number; new_users: number; updated_users: number; errors: number } }> {
    return this.request('/sync-users', {
      method: 'POST',
    })
  }

  // Business Units
  async getBusinessUnits(params?: {
    includeInactive?: boolean
  }): Promise<BusinessUnit[]> {
    const searchParams = new URLSearchParams()
    if (params?.includeInactive) searchParams.set('includeInactive', 'true')

    const query = searchParams.toString()
    return this.request<BusinessUnit[]>(
      `${apiConfig.endpoints.businessUnits}${query ? `?${query}` : ''}`
    )
  }

  async getBusinessUnit(id: number): Promise<BusinessUnit> {
    return this.request<BusinessUnit>(apiConfig.endpoints.businessUnitById(id))
  }

  async createBusinessUnit(data: BusinessUnitFormData): Promise<BusinessUnit> {
    return this.request<BusinessUnit>(apiConfig.endpoints.businessUnits, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async updateBusinessUnit(id: number, data: BusinessUnitFormData): Promise<BusinessUnit> {
    return this.request<BusinessUnit>(apiConfig.endpoints.businessUnitById(id), {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async deleteBusinessUnit(id: number): Promise<void> {
    await this.request<void>(apiConfig.endpoints.businessUnitById(id), {
      method: 'DELETE',
    })
  }

  // Risk Levels
  async getRiskLevels(): Promise<RiskLevel[]> {
    return this.request<RiskLevel[]>(apiConfig.endpoints.riskLevels)
  }

  // Audit Log
  async getAuditLog(params?: {
    page?: number
    pageSize?: number
    entityType?: string
    entityId?: number
    action?: string
    startDate?: string
    endDate?: string
  }): Promise<PagedResponse<AuditLogEntry>> {
    const searchParams = new URLSearchParams()
    if (params?.page) searchParams.set('page', params.page.toString())
    if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString())
    if (params?.entityType) searchParams.set('entityType', params.entityType)
    if (params?.entityId) searchParams.set('entityId', params.entityId.toString())
    if (params?.action) searchParams.set('action', params.action)
    if (params?.startDate) searchParams.set('startDate', params.startDate)
    if (params?.endDate) searchParams.set('endDate', params.endDate)

    const query = searchParams.toString()
    return this.request<PagedResponse<AuditLogEntry>>(
      `${apiConfig.endpoints.auditLog}${query ? `?${query}` : ''}`
    )
  }

  // Settings
  async getSettings(category?: string): Promise<Array<{
    settingId: number
    category: string
    key: string
    value: string
    dataType: string
    description?: string
  }>> {
    const params = category ? `?category=${encodeURIComponent(category)}` : ''
    return this.request(`/settings${params}`)
  }

  async updateSetting(category: string, key: string, value: string): Promise<void> {
    await this.request('/settings', {
      method: 'PUT',
      body: JSON.stringify({ category, key, value }),
    })
  }

  async recalculateRiskLevels(): Promise<{ applicationsUpdated: number }> {
    return this.request('/settings/recalculate-risk', {
      method: 'POST',
    })
  }

  // Application Sync
  async syncApplications(): Promise<{
    success: boolean
    totalApplications: number
    newApplications: number
    updatedApplications: number
    deletedApplications: number
    totalCredentials: number
    deletedCredentials: number
    durationSeconds: number
    snapshotPath?: string
    syncType: string
    triggeredBy: string
    errorMessage?: string
    errors?: string[]
  }> {
    return this.request('/sync/applications', {
      method: 'POST',
    })
  }

  async getSyncStatus(): Promise<{
    auditLogId?: number
    lastSyncAt?: string
    details?: {
      totalApplications: number
      newApplications: number
      updatedApplications: number
      deletedApplications: number
      totalCredentials: number
      deletedCredentials: number
      durationSeconds: number
      syncType: string
      triggeredBy: string
    }
  } | null> {
    return this.request('/sync/status')
  }

  // Reports
  async getApplicationTrends(params?: {
    startDate?: string
    endDate?: string
    interval?: 'daily' | 'weekly' | 'monthly'
  }): Promise<TrendData> {
    const searchParams = new URLSearchParams()
    if (params?.startDate) searchParams.set('startDate', params.startDate)
    if (params?.endDate) searchParams.set('endDate', params.endDate)
    if (params?.interval) searchParams.set('interval', params.interval)

    const query = searchParams.toString()
    return this.request<TrendData>(`/reports/trends${query ? `?${query}` : ''}`)
  }

  async getApplicationChanges(days: number = 30): Promise<ApplicationChangesData> {
    return this.request<ApplicationChangesData>(`/reports/changes?days=${days}`)
  }

  async getRecentChanges(limit = 14, lookback = 30): Promise<RecentChangeFeedData> {
    return this.request<RecentChangeFeedData>(`/reports/recent-changes?limit=${limit}&lookback=${lookback}`)
  }

  /** One day's full change set for the dashboard click-a-day drill-down panel. */
  async getChangeDetail(date: string): Promise<ChangeDetail> {
    return this.request<ChangeDetail>(`/reports/change-detail?date=${encodeURIComponent(date)}`)
  }

  /** Estate posture "at a glance" for the dashboard posture panel (V3). */
  async getPosture(): Promise<PostureData> {
    return this.request<PostureData>('/reports/posture')
  }

  /** Last-successful-sync health for the dashboard sync chip + stale banner (V4). */
  async getSyncHealth(): Promise<SyncHealth> {
    return this.request<SyncHealth>('/reports/sync-health')
  }

  async getAcknowledgments(date: string): Promise<{ date: string; acknowledgments: ChangeAcknowledgment[] }> {
    return this.request(`/reports/acknowledge?date=${encodeURIComponent(date)}`)
  }

  async createAcknowledgment(
    date: string,
    note?: string,
    ticketNumber?: string,
  ): Promise<ChangeAcknowledgment> {
    return this.request<ChangeAcknowledgment>('/reports/acknowledge', {
      method: 'POST',
      body: JSON.stringify({ date, note: note || undefined, ticketNumber: ticketNumber || undefined }),
    })
  }

  /** E4 — CA Coverage: per-app MFA policy coverage analysis. */
  async getCACoverage(): Promise<CACoverageData> {
    return this.request<CACoverageData>('/reports/ca-coverage')
  }

  /** E4 — CA Coverage CSV export URL (opened in a new tab / anchor download). */
  getCACoverageCsvUrl(): string {
    return `${apiConfig.baseUrl}/reports/ca-coverage?format=csv`
  }

  // Synthetic Data
  async cleanupSyntheticData(): Promise<{ deleted: number; errors: number; total_found: number }> {
    return this.request<{ deleted: number; errors: number; total_found: number }>(
      '/synthetic/cleanup',
      { method: 'POST' },
    )
  }

  async runSyntheticDataNow(): Promise<{
    action: string
    added: number
    modified: number
    removed: number
    details: string[]
    existing_before?: number
  }> {
    return this.request('/synthetic/run', { method: 'POST' })
  }
}

export const apiService = new ApiService()
